import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, View, ActivityIndicator, Image, LayoutChangeEvent, Text } from 'react-native';
import Card from '../shared/card/Card';
import Canvas from '../canvas';
import ScreenStatus from '../home/ScreenStatus';
import { useFormsStore } from '../../stores/onboarding';
import CustomButton from "../shared/button/Button"

interface PageItem {
    pageNumber: number;
    uri: string;
    ratio: number;
}

interface SignatureFormViewProps {
    essUserOnBoardingFormKey: string;
    essUserOnBoardingKey: string;
    onPrevious: () => void;
    onSubmit: (signatureDataURL: string) => void;
    isSubmitting: boolean;
    stateValue?: string;
    previousText?: string;
    submitText?: string;
    totalPages?: number;
}

const SignatureFormView: React.FC<SignatureFormViewProps> = ({
    essUserOnBoardingFormKey,
    essUserOnBoardingKey,
    onPrevious,
    onSubmit,
    isSubmitting,
    stateValue,
    previousText = "Previous",
    submitText = "Submit",
    totalPages
}) => {
    const { getImagePreviewUri } = useFormsStore();

    const isSMM = stateValue === 'SMM';
    const resolvedTotalPages = totalPages || (isSMM ? 31 : 1);
    const isPaginated = isSMM || (totalPages !== undefined && totalPages > 1);

    const [hasDrawn, setHasDrawn] = useState(false);
    const [signatureDataURL, setSignatureDataURL] = useState('');
    const [imageUri, setImageUri] = useState<string | null>(null);
    const [pages, setPages] = useState<PageItem[]>([]);
    const [isLoadingInitial, setIsLoadingInitial] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [imageRatio, setImageRatio] = useState(0);

    const loadingRef = useRef(false);
    const currentPageRef = useRef(1);

    useEffect(() => {
        let isMounted = true;
        setError(null);

        if (isPaginated) {
            setIsLoadingInitial(true);
            setPages([]);
            currentPageRef.current = 1;
            loadingRef.current = true;

            const loadFirstPage = async () => {
                try {
                    const data = await getImagePreviewUri(
                        essUserOnBoardingFormKey,
                        essUserOnBoardingKey,
                        stateValue,
                        1
                    );
                    if (!isMounted) return;

                    if (!data) {
                        setError("Something went wrong");
                        throw new Error("No image data received");
                    }

                    const blob = new Blob([data], { type: 'image/png' });
                    const url = URL.createObjectURL(blob);

                    Image.getSize(
                        url,
                        (imgWidth, imgHeight) => {
                            if (!isMounted) return;
                            setPages([{ pageNumber: 1, uri: url, ratio: imgHeight / imgWidth }]);
                            setIsLoadingInitial(false);
                            loadingRef.current = false;
                        },
                        (err) => {
                            console.log('Image.getSize error:', err);
                            if (!isMounted) return;
                            setPages([{ pageNumber: 1, uri: url, ratio: 1.414 }]);
                            setIsLoadingInitial(false);
                            loadingRef.current = false;
                        }
                    );
                } catch (err: any) {
                    if (!isMounted) return;
                    console.error("Image fetch error:", err);
                    setError(err.message || "Something went wrong");
                    setIsLoadingInitial(false);
                    loadingRef.current = false;
                }
            };

            loadFirstPage();
        } else {
            const getImage = async () => {
                try {
                    const data = await getImagePreviewUri(
                        essUserOnBoardingFormKey,
                        essUserOnBoardingKey,
                        stateValue
                    );
                    if (!isMounted) return;

                    if (!data) {
                        setError("Something went wrong");
                        throw new Error("No image data received");
                    }

                    const blob = new Blob([data], { type: 'image/png' });
                    const url = URL.createObjectURL(blob);
                    setImageUri(url);
                } catch (err: any) {
                    if (!isMounted) return;
                    console.error("Image fetch error:", err);
                    setError(err.message || "Something went wrong");
                }
            };

            getImage();
        }

        return () => {
            isMounted = false;
        };
    }, [essUserOnBoardingFormKey, essUserOnBoardingKey, stateValue, isPaginated]);

    useEffect(() => {
        if (!isPaginated && imageUri) {
            Image.getSize(
                imageUri,
                (imgWidth, imgHeight) => {
                    setImageRatio(imgHeight / imgWidth);
                },
                (error) => {
                    console.log('Image.getSize error:', error);
                    setImageRatio(1.414); // A4 fallback
                    setError("Something went wrong");
                }
            );
        }
    }, [imageUri, isPaginated]);

    const loadNextPage = async () => {
        if (loadingRef.current) return;
        const nextPage = currentPageRef.current + 1;
        if (nextPage > resolvedTotalPages) return;

        loadingRef.current = true;
        setIsLoadingMore(true);

        try {
            const data = await getImagePreviewUri(
                essUserOnBoardingFormKey,
                essUserOnBoardingKey,
                stateValue,
                nextPage
            );

            if (!data) {
                loadingRef.current = false;
                setIsLoadingMore(false);
                return;
            }

            const blob = new Blob([data], { type: 'image/png' });
            const url = URL.createObjectURL(blob);

            Image.getSize(
                url,
                (imgWidth, imgHeight) => {
                    currentPageRef.current = nextPage;
                    setPages((prev) => [...prev, { pageNumber: nextPage, uri: url, ratio: imgHeight / imgWidth }]);
                    loadingRef.current = false;
                    setIsLoadingMore(false);
                },
                (err) => {
                    console.log('Image.getSize error:', err);
                    currentPageRef.current = nextPage;
                    setPages((prev) => [...prev, { pageNumber: nextPage, uri: url, ratio: 1.414 }]);
                    loadingRef.current = false;
                    setIsLoadingMore(false);
                }
            );
        } catch (err: any) {
            console.error(`Error loading page ${nextPage}:`, err);
            loadingRef.current = false;
            setIsLoadingMore(false);
        }
    };

    const handleScroll = (event: any) => {
        if (!isPaginated) return;
        const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
        const paddingToBottom = 300;
        const isClose = layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom;
        if (isClose) {
            loadNextPage();
        }
    };

    const handleContainerLayout = (event: LayoutChangeEvent) => {
        const width = event.nativeEvent.layout.width;
        if (width > 0) {
            setContainerWidth(width);
        }
    };

    const imageHeight = containerWidth > 0 && imageRatio > 0 ? containerWidth * imageRatio : 0;

    if (error) return <ScreenStatus message={error} />;

    return (
        <ScrollView
            bounces={false}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 30 }}
            onScroll={handleScroll}
            scrollEventThrottle={16}
        >
            <Card
                style={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '11px',
                    padding: 0,
                    margin: 0,
                    overflow: 'hidden',
                }}
            >
                <View style={{ width: '100%' }} onLayout={handleContainerLayout}>
                    {isPaginated ? (
                        isLoadingInitial || pages.length === 0 ? (
                            <ActivityIndicator style={{ marginVertical: 40 }} />
                        ) : (
                            <>
                                {pages.map((page) => {
                                    const pageHeight =
                                        containerWidth > 0 && page.ratio > 0
                                            ? containerWidth * page.ratio
                                            : 0;
                                    return (
                                        <View key={`page-${page.pageNumber}`} style={{ width: '100%' }}>
                                            {pageHeight > 0 && (
                                                <Image
                                                    source={{ uri: page.uri }}
                                                    style={{
                                                        width: containerWidth,
                                                        height: pageHeight,
                                                    }}
                                                    resizeMode="contain"
                                                />
                                            )}
                                        </View>
                                    );
                                })}

                                {isLoadingMore && (
                                    <View style={{ paddingVertical: 20, alignItems: 'center' }}>
                                        <ActivityIndicator size="small" />
                                        <Text style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                                           Loading next page...
                                        </Text>
                                    </View>
                                )}

                                {pages.length >= resolvedTotalPages && (
                                    <View style={{ width: 350, height: 150, margin: 15, marginBottom: 80 }}>
                                        <Text style={{ padding: 2, marginLeft: 10 }}>
                                            Signature Of Employee:{' '}
                                        </Text>
                                        <Canvas
                                            setSignatureDataURL={setSignatureDataURL}
                                            setHasDrawn={setHasDrawn}
                                            hasDrawn={hasDrawn}
                                        />
                                    </View>
                                )}
                            </>
                        )
                    ) : (
                        imageHeight > 0 ? (
                            <>
                                <Image
                                    source={{ uri: imageUri as string }}
                                    style={{
                                        width: containerWidth,
                                        height: imageHeight,
                                    }}
                                    resizeMode="contain"
                                />

                                <View style={{ width: 350, height: 150, margin: 15, marginBottom: 80 }}>
                                    <Text style={{ padding: 2, marginLeft: 10 }}>
                                        Signature Of Employee:{' '}
                                    </Text>
                                    <Canvas
                                        setSignatureDataURL={setSignatureDataURL}
                                        setHasDrawn={setHasDrawn}
                                        hasDrawn={hasDrawn}
                                    />
                                </View>
                            </>
                        ) : (
                            <ActivityIndicator style={{ marginVertical: 40 }} />
                        )
                    )}
                </View>
            </Card>

            {isSubmitting ? (
                <ActivityIndicator style={{ marginTop: 20 }} />
            ) : (
                <View
                    style={{
                        marginTop: 10,
                        marginBottom: 25,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        paddingHorizontal: 16,
                    }}
                >
                    <CustomButton
                        text={previousText}
                        color="warn"
                        onPress={onPrevious}
                    />
                    <CustomButton
                        isDisabled={!signatureDataURL || !hasDrawn}
                        text={submitText}
                        onPress={() => onSubmit(signatureDataURL)}
                        color='success'
                    />
                </View>
            )}
        </ScrollView>
    );
};

export default SignatureFormView;